import {
  useGetPartnerQuery,
  useSetHasRunTourMutation,
} from '@/api/api.slice.tsx';
import { useAppDispatch, useAppSelector } from '@/app/hooks.ts';
import {
  getTutorialState,
  setRunTour,
  setStepIndex,
} from '@/features/Tutorial/Tutorial.slice.ts';
import { Typography } from '@arsnovasystems/ars-front-lib';
import { useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Joyride, {
  ACTIONS,
  CallBackProps,
  Events,
  EVENTS,
  ORIGIN,
  Status,
  STATUS,
  Step,
} from 'react-joyride';

/**
 * Tutorial Tour component
 *
 * @returns JSX of the component
 */
export const TutorialTour = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { runTour, stepIndex } = useAppSelector(getTutorialState);
  const [setHasRunTourMutation] = useSetHasRunTourMutation();

  const { data: me } = useGetPartnerQuery();

  const steps = [
    {
      content: (
        <Typography variant={'h4'}>{t('ik.tutorialTour.welcome')}</Typography>
      ),
      placement: 'center',
      target: 'body',
      showSkipButton: true,
      locale: {
        skip: t('ik.tutorialTour.skip'),
      },
    },
    {
      content: t('ik.tutorialTour.1'),
      placement: 'bottom',
      target: '[data-tour-selector="help-menu-button"]',
      showSkipButton: true,
    },
    {
      content: t('ik.tutorialTour.2'),
      placement: 'bottom',
      target: '[data-tour-selector="menu-customer-accounts"]',
      showSkipButton: true,
    },
    {
      content: t('ik.tutorialTour.3'),
      placement: 'bottom',
      target: '[data-tour-selector="menu-invoices"]',
      showSkipButton: true,
    },
    {
      content: t('ik.tutorialTour.4'),
      placement: 'bottom',
      target: '[data-tour-selector="menu-partner-info"]',
      showSkipButton: true,
    },
    {
      content: t('ik.tutorialTour.5'),
      placement: 'bottom',
      target: '[data-tour-selector="menu-means-of-payments"]',
      showSkipButton: true,
    },
    {
      content: t('ik.tutorialTour.6'),
      placement: 'top-start',
      target: '[data-tour-selector="grid-card-switcher"]',
      showSkipButton: true,
    },
    {
      content: t('ik.tutorialTour.7'),
      placement: 'top',
      target: '[data-tour-selector="grid-card-paginate"]',
      showSkipButton: true,
    },
    {
      content: t('ik.tutorialTour.8'),
      placement: 'bottom',
      target: '[data-tour-selector="advanced-search-basic"]',
    },
  ] as Step[];

  useEffect(() => {
    if (!me) return;
    if (!me.hasRunTour) {
      dispatch(setRunTour(true));
    }
  }, [dispatch, me]);

  const setHasRunTour = () => {
    void (async () => await setHasRunTourMutation(true))();
  };

  const onCallback = (data: CallBackProps) => {
    const { action, index, origin, status, type } = data;

    if (action === ACTIONS.CLOSE && origin === ORIGIN.KEYBOARD) {
      dispatch(setRunTour(false));
      dispatch(setStepIndex(0));
    }

    if (
      ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as Events[]).includes(type)
    ) {
      // Update state to advance the tour
      dispatch(setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1)));
    } else if (
      ([STATUS.FINISHED, STATUS.SKIPPED] as Status[]).includes(status)
    ) {
      // You need to set our running state to false, so we can restart if we click start again.
      dispatch(setRunTour(false));
      dispatch(setStepIndex(0));
      setHasRunTour();
    }
  };

  const theme = useTheme();

  return (
    <Joyride
      steps={steps}
      stepIndex={stepIndex}
      callback={onCallback}
      run={runTour}
      styles={{
        options: {
          primaryColor: theme.palette.primary.main,
        },
      }}
      locale={{
        next: t('ik.tutorialTour.next'),
        back: t('ik.tutorialTour.back'),
        skip: t('ik.close'),
        last: t('ik.finish'),
      }}
      hideCloseButton={true}
      disableCloseOnEsc={true}
      disableOverlayClose={true}
      continuous
    />
  );
};
